package com.devi3east.bashbuddy.search.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.devi3east.bashbuddy.features.events.common.EventRepository
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.common.data.models.EventStatus
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class SearchViewModel(
    private val repository: EventRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(SearchUiState())
    val uiState: StateFlow<SearchUiState> = _uiState

    private val _query = MutableStateFlow("")
    val query: StateFlow<String> = _query

    private val _filters = MutableStateFlow(SearchFilters())
    val filters: StateFlow<SearchFilters> = _filters

    private var allEvents: List<Event> = emptyList()

    private val _availableStates = MutableStateFlow<List<String>>(emptyList())
    val availableStates: StateFlow<List<String>> = _availableStates

    private fun updateAvailableStates() {
        _availableStates.value = allEvents
            .mapNotNull { it.address?.state }
            .distinct()
            .sorted()
    }

    fun getEventsList() {
        viewModelScope.launch {

            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = null
                )
            }

            val result = repository.getEventsList()

            result.onSuccess { response ->
                val filteredEvents = response.events.filter {
                    it.publishedAtInstant != null
                }


                allEvents = filteredEvents

                updateAvailableStates()

                applyFilters()

                _uiState.update {
                    it.copy(isLoading = false)
                }

            }.onFailure { e ->

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = e.message ?: "Unknown error occurred"
                    )
                }

            }
        }
    }

    fun clearFiltersAndQuery() {
        _filters.value = SearchFilters()
        _query.value = ""
        applyFilters()
    }

    fun onSearchQueryChanged(newQuery: String) {
        _query.value = newQuery
        applyFilters()
    }

    fun updateFilters(newFilters: SearchFilters) {
        _filters.value = newFilters
        applyFilters()
    }

    private fun applyFilters() {

        val query = _query.value
        val filters = _filters.value

        val filteredEvents = allEvents
            .asSequence()

            .filter {
                it.status == EventStatus.UPCOMING ||
                        it.status == EventStatus.LIVE ||
                        it.status == EventStatus.COMPLETED
            }

            // QUERY FILTER (works with both Event + Organization)
            .filter { event ->

                if (query.isBlank()) return@filter true

                when (filters.filterType) {

                    SearchFilterType.EVENT_TITLE ->
                        event.title?.contains(query, ignoreCase = true) == true

                    SearchFilterType.ORGANIZATION ->
                        event.createdByName.contains(query, ignoreCase = true)

                    else ->
                        event.title?.contains(query, ignoreCase = true) == true ||
                                event.createdByName.contains(query, ignoreCase = true)
                }
            }

            // STATE FILTER
            .filter { event ->
                filters.state == null ||
                        event.address?.state?.equals(filters.state, ignoreCase = true) == true
            }

            // MONTH FILTER
            .filter { event ->

                if (filters.month == null) return@filter true

                val instant = event.startInstant ?: return@filter false

                val month =
                    instant.toLocalDateTime(TimeZone.currentSystemDefault()).monthNumber

                month == filters.month
            }

            // DATE FILTER
            .filter { event ->

                val filterInstant = filters.date ?: return@filter true

                val start = event.startInstant ?: return@filter false
                val end = event.endInstant ?: start

                val filterDay = filterInstant.toEpochMilliseconds() / 86_400_000L
                val startDay = start.toEpochMilliseconds() / 86_400_000L
                val endDay = end.toEpochMilliseconds() / 86_400_000L

                filterDay in startDay..endDay
            }

            .toList()

        _uiState.update {
            it.copy(eventsList = filteredEvents)
        }
    }

}